import { Box, Button, Stack, Typography } from '@mui/material'

import { UserPaymentType } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  isKonbini: boolean
  purchaseOrderId: string
  isShowUsageTicketNote: boolean
  pendingPurchaseId: string
  paymentType?: UserPaymentType | null
  voucherUrl: string | null
  paymentDeadline: string | null
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const FirstComeFirstServedCompleteTemplate: Template<State, Action> = ({
  state,
}) => {
  const { t } = useTranslation('firstComeFirstServedComplete')
  const { routerNames } = useRouter()

  return (
    <BaseLayout
      margin
      header={{
        title: state.loading
          ? ''
          : state.isKonbini
          ? t('titleKonbini')
          : t('title'),
      }}
    >
      <Head title={routerNames.FirstComeFirstServedComplete} />
      {!state.loading && (
        <ResultCell
          isKonbini={state.isKonbini}
          paymentDeadline={state.paymentDeadline ?? ''}
          isShowUsageTicketNote={state.isShowUsageTicketNote}
        />
      )}
      {!state.loading && (
        <ButtonCell
          purchaseOrderId={state.purchaseOrderId}
          pendingPurchaseId={state.pendingPurchaseId}
          isKonbini={state.isKonbini}
          voucherUrl={state.voucherUrl}
        />
      )}
    </BaseLayout>
  )
}

const ResultCell: Cell<{
  isKonbini: boolean
  paymentDeadline: string
  isShowUsageTicketNote: boolean
}> = ({ isKonbini, paymentDeadline, isShowUsageTicketNote }) => {
  const { t } = useTranslation('firstComeFirstServedComplete')

  return (
    <CellFrame sx={{ pt: 6, ...(isKonbini ? { pb: 5 } : {}) }}>
      <Typography variant="h5" align="center" sx={isKonbini ? {} : { pb: 1 }}>
        {t(isKonbini ? 'completeMessageKonbini' : 'completeMessage')}
      </Typography>
      <Typography
        variant="body2"
        align="center"
        sx={isKonbini ? { pt: 1, pb: 6 } : { pt: 1, pb: 5 }}
      >
        {t(isKonbini ? 'completeDescMessageKonbini' : 'completeDescMessage')}
      </Typography>
      {isKonbini && (
        <Box
          sx={{
            borderColor: 'error.main',
            borderWidth: 1,
            borderStyle: 'solid',
            py: 2,
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" color="error.main" align="center">
            {t('paymentDeadline', { date: paymentDeadline })}
          </Typography>
        </Box>
      )}
    </CellFrame>
  )
}

const ButtonCell: Cell<{
  purchaseOrderId: string
  isKonbini: boolean
  pendingPurchaseId: string
  voucherUrl?: string | null
}> = ({ purchaseOrderId, pendingPurchaseId, voucherUrl, isKonbini }) => {
  const { t } = useTranslation('firstComeFirstServedComplete')
  const { routerPages, router, generatePath } = useRouter()
  const { onClick: onClickVoucherUrl } = useBrowserLink(voucherUrl ?? '')

  return (
    <CellFrame>
      <Stack spacing={4} alignItems="center" sx={{ py: 5 }}>
        {isKonbini ? (
          <>
            <Button
              variant="contained"
              size="large"
              sx={{ width: 244 }}
              onClick={onClickVoucherUrl}
            >
              {t('proceedPaymentProcedure', '決済手順へすすむ')}
            </Button>
            <Button
              variant="text"
              size="large"
              sx={{ width: 244, py: 1, height: 'auto' }}
              href={generatePath(routerPages.PaymentStatusDetail, {
                pendingPurchaseId,
              })}
            >
              {t('paymentStatus', '決済状況を確認する')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              size="large"
              sx={{ width: 244 }}
              onClick={() =>
                router.replace(generatePath(routerPages.StorePurchaseOrders))
              }
            >
              {t('storePurchaseHistory')}
            </Button>
            <Button
              variant="text"
              sx={{ paddingTop: '0px' }}
              href={routerPages.Top}
            >
              {t('topPage')}
            </Button>
          </>
        )}
      </Stack>
    </CellFrame>
  )
}
